import ApiService from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import { assertCanManageActionPlan, getAuthUser } from '@/shared/auth/obligationAuth';

/**
 * ActionPlanService - Servis za upravljanje akcionim planovima
 * 
 * Omogućava komunikaciju sa Action Plan API endpoint-ima:
 * - POST /action-plan/search - Pretraga akcionih planova (sa ili bez action_plan_id)
 * - POST /action-plan/status/change - Promena statusa akcionog plana
 * - POST /action-plan/save-or-update - Dodavanje/editovanje akcionog plana
 */
class ActionPlanService {
    /**
     * Pretražuje akcione planove
     * @returns {Promise<Object>} Objekat sa action_plans nizom
     */
    async search() {
        try {
            const response = await ApiService.post(API_ENDPOINTS.OGP.ACTION_PLAN.SEARCH, {});
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Pretražuje akcione planove za početnu (draft, implementation, completed za arhivu).
     * @returns {Promise<Object>} { draft: object|null, implementation: object|null, completed: array }
     */
    async searchV2(signal = null) {
        try {
            const response = await ApiService.post(
                API_ENDPOINTS.OGP.ACTION_PLAN.SEARCH_V2,
                {},
                signal,
            );
            return response.data ?? { draft: null, implementation: null, completed: [] };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Pretražuje istoriju promena statusa za akcioni plan
     * @param {string} actionPlanId - ID akcionog plana
     * @returns {Promise<Object>} Objekat sa history nizom
     */
    async searchHistory(actionPlanId) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.OGP.ACTION_PLAN.SEARCH, {
                action_plan_id: actionPlanId
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Dobija istoriju promena statusa za akcioni plan
     * @param {string} actionPlanId - ID akcionog plana
     * @returns {Promise<Object>} Objekat sa istorijom statusa
     */
    async getStatusHistory(actionPlanId) {
        try {
            const response = await ApiService.post(API_ENDPOINTS.OGP.ACTION_PLAN.STATUS_HISTORY, {
                action_plan_id: actionPlanId
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Menja status akcionog plana
     * @param {string} actionPlanId - ID akcionog plana
     * @param {string} status - DRAFT | IMPLEMENTATION | REVISION | COMPLETED
     * @param {string} comment - Opcioni komentar promene statusa
     * @returns {Promise<Object>} Odgovor servera
     */
    async changeStatus(actionPlanId, status, comment = '') {
        try {
            const user = await getAuthUser();
            assertCanManageActionPlan(user);
            const payload = {
                action_plan_id: actionPlanId,
                status: status
            };
            if (comment && String(comment).trim()) {
                payload.comment = String(comment).trim();
            }
            const response = await ApiService.post(API_ENDPOINTS.OGP.ACTION_PLAN.STATUS_CHANGE, {
                ...payload
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Čuva ili ažurira akcioni plan
     * @param {Object} actionPlan - Akcioni plan koji se čuva/ažurira
     * @param {string} actionPlan.id - ID akcionog plana (opciono za novi plan)
     * @param {string} actionPlan.name - Naziv akcionog plana
     * @param {string} actionPlan.planned_draft_start - Planirani početak izrade (ISO format)
     * @param {string} actionPlan.planned_draft_end - Planirani završetak izrade (ISO format)
     * @param {string} actionPlan.planned_implementation_start - Planirani početak implementacije (ISO format)
     * @param {string} actionPlan.planned_implementation_end - Planirani završetak implementacije (ISO format)
     * @returns {Promise<Object>} Sačuvani akcioni plan
     */
    async saveOrUpdate(actionPlan) {
        try {
            const user = await getAuthUser();
            assertCanManageActionPlan(user);
            const response = await ApiService.post(API_ENDPOINTS.OGP.ACTION_PLAN.SAVE_OR_UPDATE, actionPlan);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
}

const actionPlanServiceInstance = new ActionPlanService();
export default actionPlanServiceInstance;
